import React from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { Button } from '../common';
import { Heading, Text, Typography } from '../common/Typography';
import { Card } from '../common/Card';
import { homeSectionHeading, homeElevatedInset } from './homeDashboardStyles';
import {
  buildEventHeadline,
  buildEventSubline,
  getEventIcon,
  getLadderLabel,
  isMatchupEvent,
} from '../../services/activityFeedService';
import {
  getActivityMomentCta,
  navigateForActivityMoment,
} from '../../utils/activityMomentNavigation';
import { NotableWinSubline } from '../activity/NotableWinSubline';
import ClubLogo from '../clubs/ClubLogo';
import { interactiveBleedInsetX } from '../../constants/interactiveSafeInsets';

const homeMomentsStripClass = `flex gap-4 overflow-x-auto pb-3 pt-1 ${interactiveBleedInsetX} scroll-smooth snap-x snap-mandatory sm:snap-none [scrollbar-gutter:stable_both-edges] touch-pan-x`;
const homeMomentsCardClass = 'shrink-0 w-[min(100%,18.5rem)] sm:w-72 lg:w-80 snap-start';

/**
 * Maps event_type to a Tailwind colour pair [bg, text] for the icon chip.
 */
const EVENT_COLOURS = {
  personal_best:      ['bg-warning-light', 'text-warning-dark'],
  rank_up:            ['bg-info-light', 'text-info-dark'],
  overtook_player:    ['bg-primary-light', 'text-primary'],
  undefeated_session: ['bg-success-light', 'text-success-dark'],
  great_session:      ['bg-warning-light', 'text-warning-dark'],
  biggest_mover:      ['bg-success-light', 'text-success-dark'],
  rivalry_swing:          ['bg-info-light', 'text-info-dark'],
  rivalry_heated:         ['bg-success-light', 'text-success-dark'],
  rivalry_nemesis:        ['bg-surface-subtle', 'text-text-secondary'],
  rivalry_dominated:      ['bg-info-light', 'text-info-dark'],
  rivalry_emerging:       ['bg-success-light', 'text-success-dark'],
  matchup_first_win:      ['bg-success-light', 'text-success-dark'],
  matchup_tied:           ['bg-info-light', 'text-info-dark'],
  matchup_took_lead:      ['bg-info-light', 'text-info-dark'],
  matchup_hot_form:       ['bg-success-light', 'text-success-dark'],
  matchup_frequent_recent:['bg-surface-subtle', 'text-text-secondary'],
  upset_win:              ['bg-danger-light', 'text-danger-dark'],
  close_game_loss:        ['bg-surface-subtle', 'text-text-secondary'],
  session_new_opponent:   ['bg-info-light', 'text-info-dark'],
  session_played_with:    ['bg-surface-subtle', 'text-text-secondary'],
};

function getEventColours(eventType) {
  return EVENT_COLOURS[eventType] ?? ['bg-surface-subtle', 'text-text-secondary'];
}

/**
 * Single activity moment card.
 */
const homeMomentCardCtaClass = 'w-full text-xs font-semibold';

function MomentCard({ event, onNavigate, currentUser }) {
  const [bgClass, textClass] = getEventColours(event.event_type);
  const icon = getEventIcon(event.event_type);
  const headline = buildEventHeadline(event);
  const subline = buildEventSubline(event);
  const isNotableWin = event.event_type === 'upset_win';
  const isMatchup = isMatchupEvent(event.event_type);
  const momentCta = getActivityMomentCta(event, { currentUser });
  const ladderLabel = isMatchup
    ? 'Matchup'
    : event.metadata?.ladder_type
      ? getLadderLabel(event.metadata.ladder_type)
      : null;
  const dateStr = event.occurred_at
    ? format(parseISO(event.occurred_at), 'MMM d, yyyy')
    : null;

  return (
    <Card
      variant="default"
      className={`flex flex-col gap-3 ${homeElevatedInset} ${homeMomentsCardClass}`}
    >
      {/* Icon chip + type label */}
      <div className="flex items-center gap-2.5">
        <span
          className={`inline-flex items-center justify-center w-9 h-9 rounded-xl text-lg shrink-0 ${bgClass} ${textClass}`}
          aria-hidden
        >
          {icon}
        </span>
        <div className="min-w-0">
          <Typography variant="caption" className="text-text-muted uppercase tracking-wide text-[0.65rem] font-semibold">
            {ladderLabel ?? 'Moment'}
          </Typography>
        </div>
      </div>

      {/* Headline */}
      <Typography className="font-semibold leading-snug text-text-primary text-sm line-clamp-3">
        {headline}
      </Typography>

      {isNotableWin ? (
        <NotableWinSubline event={event} />
      ) : subline ? (
        <Typography variant="caption" className="text-text-secondary leading-snug line-clamp-4">
          {subline}
        </Typography>
      ) : null}

      {/* Date + club */}
      {(dateStr || event.club_name) && (
        <div className="flex items-end justify-between gap-2 mt-auto min-w-0">
          {dateStr ? (
            <Typography variant="caption" className="text-text-muted shrink-0">
              {dateStr}
            </Typography>
          ) : (
            <span />
          )}
          {event.club_name ? (
            <div className="flex items-center gap-1.5 min-w-0 max-w-[55%]" title={event.club_name}>
              {event.club_logo_image_url && (
                <ClubLogo
                  logoImageUrl={event.club_logo_image_url}
                  clubName={event.club_name}
                  size="sm"
                  className="shrink-0"
                />
              )}
              <Typography
                variant="caption"
                className="text-text-muted truncate text-right min-w-0"
              >
                {event.club_name}
              </Typography>
            </div>
          ) : null}
        </div>
      )}

      {momentCta ? (
        <div className="mt-auto">
          <Button
            variant="outline"
            size="small"
            className={homeMomentCardCtaClass}
            onClick={() => onNavigate(event)}
          >
            {momentCta.label}
          </Button>
        </div>
      ) : null}
    </Card>
  );
}

/**
 * HomeActivityFeed — displays the player's persisted activity moments
 * (rank_up, personal_best, overtook_player, etc.) from the activity_events table.
 *
 * Replaces the session win-rate strip (HomeActivity) on the homepage.
 * Moments are generated once at session completion and never recalculated on read.
 */
export function HomeActivityFeed({ items, currentUser, loading }) {
  const navigate = useNavigate();

  function handleNavigate(event) {
    navigateForActivityMoment(navigate, event, currentUser);
  }

  if (loading) {
    return (
      <div className="mb-10">
        <Heading level={2} className={`${homeSectionHeading} mb-5`}>Your Moments</Heading>
        <div className="text-center py-8 rounded-2xl border border-border-medium bg-surface-subtle/60">
          <Typography variant="muted">Loading moments...</Typography>
        </div>
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <div className="mb-10">
        <Heading level={2} className={`${homeSectionHeading} mb-5`}>Your Moments</Heading>
        <Card variant="default" className={`${homeElevatedInset} text-center py-8 px-6`}>
          <Text className="text-text-secondary">
            Complete a competitive session to see your highlights here.
          </Text>
        </Card>
      </div>
    );
  }

  return (
    <div className="mb-10">
      <div className="flex items-center justify-between mb-5 gap-3">
        <Heading level={2} className={homeSectionHeading}>Your Moments</Heading>
        <Button
          onClick={() => navigate('/profile/games')}
          variant="text"
          className="text-sm shrink-0 font-semibold text-primary"
        >
          View sessions
        </Button>
      </div>

      <div
        role="region"
        aria-label="Activity moments"
        className={homeMomentsStripClass}
      >
        {items.slice(0, 5).map((event) => (
          <MomentCard
            key={event.id}
            event={event}
            onNavigate={handleNavigate}
            currentUser={currentUser}
          />
        ))}
      </div>

      {items.length > 1 ? (
        <Typography variant="caption" className="mt-3 text-center text-text-muted px-2 sm:hidden">
          Swipe sideways for more · Each card has a button for more detail
        </Typography>
      ) : null}
    </div>
  );
}
